import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/store/authStore'
import { preferencesQuery } from '@/api/preferencesQuery'
import { resolveHomeTarget } from '@/router/homePages'

/**
 * The index route: decides where "/" actually lands.
 *
 * The page table, the role defaults and the reachability rule all live in
 * `@/router/homePages` — the same table the router's `RoleGuard`s are declared from.
 * That is deliberate and load-bearing (finding F13): `RoleGuard` denies by navigating
 * to "/", so if this component ever sends a member to a page their roles cannot enter,
 * the two bounce off each other forever and the member never signs in at all.
 * `resolveHomeTarget` is total and only ever returns a reachable path, which makes
 * that loop impossible rather than merely unlikely.
 */
export function HomeRedirect() {
  const { teamMember } = useAuthStore()

  // Shared definition — ProtectedRoute has usually already put this in flight the
  // moment /auth/refresh resolved, so this normally resolves from cache or joins
  // the request already running (IN-46 Phase 4.1).
  const { data: prefs, isLoading } = useQuery(preferencesQuery)

  // Show nothing while loading to avoid a flash redirect to the wrong page.
  // The query is fast (local backend) so the delay is imperceptible.
  if (isLoading) return null

  // RB-11 W9 (b), IN-164 root cause of the "cross-org-cache-isolation" e2e flake.
  // ClientsPage (like every route in `@/router/index.tsx`) is `lazyWithRecovery` —
  // AppLayout's `<Outlet />` is wrapped in its own Suspense boundary, and React keeps
  // this component's PREVIOUSLY COMMITTED output on screen while a newly-clicked lazy
  // route's chunk is still downloading, rather than unmounting it immediately. So a
  // member who clicks a nav link (e.g. "Clients") in the brief window right after
  // landing on "/" — before this component's OWN `preferencesQuery` has resolved —
  // is still rendering HomeRedirect underneath the pending transition when that query
  // finally resolves. Its `<Navigate>` then fires unconditionally and wins the race
  // against the in-flight click navigation, silently bouncing the member back to their
  // home page instead of where they just clicked. Reproduced locally: a fresh login
  // immediately followed by a nav-link click landed on /dashboard instead of /clients
  // in roughly a third of attempts, with no console error and no failed request — see
  // frontend/e2e/cross-org-cache-isolation.spec.ts's step 1 for the regression test.
  // `window.location.pathname`, not `useLocation()`, on purpose: the browser's real
  // address bar already reflects the click's target the instant history updates, which
  // is exactly the signal a component still on its stale committed render cannot get
  // from React Router's own (also still-stale, for the same reason) location context.
  if (window.location.pathname !== '/') return null

  return <Navigate to={resolveHomeTarget(teamMember?.roles ?? [], prefs?.homePage)} replace />
}
